import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

export type DraftNumberFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number;
  onValueChange: (n: number) => void;
  min?: number;
  max?: number;
  /** 비운 채 blur 할 때 쓸 값. 없으면 직전 value 유지 */
  emptyFallback?: number;
};

/**
 * type=number 입력을 비우고 다시 칠 수 있게 함.
 * 입력 중에는 초안 문자열을 쓰고, blur 때 min/max로 확정.
 */
const DraftNumberField: React.FC<DraftNumberFieldProps> = ({
  value,
  onValueChange,
  min,
  max,
  emptyFallback,
  onBlur,
  onFocus,
  inputProps,
  ...rest
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(null);
  }, [value]);

  const commit = (raw: string) => {
    const t = raw.trim();
    if (t === '' || t === '-' || t === '.' || t === '-.') {
      const fb = emptyFallback != null ? emptyFallback : value;
      let n = Number(fb);
      if (!Number.isFinite(n)) n = min != null ? min : 0;
      if (min != null) n = Math.max(min, n);
      if (max != null) n = Math.min(max, n);
      onValueChange(n);
      return;
    }
    let n = Number(t);
    if (!Number.isFinite(n)) {
      onValueChange(value);
      return;
    }
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    onValueChange(n);
  };

  return (
    <TextField
      {...rest}
      type="number"
      value={draft !== null ? draft : value}
      inputProps={inputProps}
      onFocus={(e) => {
        focusedRef.current = true;
        onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        if (raw.trim() === '' || raw === '-' || raw === '.' || raw === '-.') return;
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        // 타이핑 중에는 클램프하지 않음(예: 2 치려다 25로 가려면 중간값 허용)
        onValueChange(n);
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        const raw = draft !== null ? draft : String(e.target.value);
        setDraft(null);
        commit(raw);
        onBlur?.(e);
      }}
    />
  );
};

export default DraftNumberField;
